import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({ user: null, login: () => {}, logout: () => {}, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip auth-check on public routes
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/presentation/")) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    // Backend sets httpOnly cookie; we just remember the user profile in state.
    const r = await api.post("/auth/login", { email, password });
    setUser({ id: r.data.id, email: r.data.email, name: r.data.name, role: r.data.role, permissions: r.data.permissions });
    return r.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout endpoint may fail if cookie is already expired/invalid;
      // we proceed with local cleanup anyway so the user is signed out client-side.
    }
    setUser(false);
  };

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
